<script setup lang="ts">
  import type { QSelectProps, QTableProps } from 'quasar'

  import { useQuery } from '@tanstack/vue-query'

  import {
    type RoleEnum,
    type UserProfileRoleOut,
    listDepartmentsPassportIdmIdmPrivateListDepartmentsPost,
  } from '@/shared/api/intranet'
  import { getImage } from '@/shared/lib/media'
  import { UButton } from '@/shared/ui/components'
  import { ChevronRighIcon, PlusIcon, SearchIcon } from '@/shared/ui/svg'

  import { listUserProfilesFullPage, UserAvatar } from '@/entities/profile'

  definePage({
    meta: {
      layout: 'admin',
    },
  })

  const router = useRouter()

  const pagination = ref({
    page: 1,
    rowsPerPage: 30,
    rowsNumber: 0,
  })
  type SearchFilters = {
    department_filter?: { ids?: string[]; show_unassigned?: boolean }
    is_blocked?: boolean
    query?: string
    role?: RoleEnum
  }
  const searchFilters = reactive<SearchFilters>({
    query: undefined,
    department_filter: undefined,
    is_blocked: false,
    role: undefined,
  })
  const handleQueryChange = (value: string | null) => {
    if (value === null) {
      searchFilters.query = undefined
    } else {
      searchFilters.query = value
    }
  }

  const handleDepartmentChange = (value: string[] | null) => {
    if (value === null) {
      searchFilters.department_filter = undefined
    } else {
      const hasUnassigned = value.includes('unassigned')
      const departmentIds = value.filter((id) => id !== 'unassigned')

      if (hasUnassigned && departmentIds.length > 0) {
        searchFilters.department_filter = { ids: departmentIds, show_unassigned: true }
      } else if (hasUnassigned) {
        searchFilters.department_filter = { show_unassigned: true }
      } else {
        searchFilters.department_filter = { ids: departmentIds }
      }
    }
  }
  const handleReturnBlockedChange = (value: boolean | null) => {
    if (value === null) {
      searchFilters.is_blocked = undefined
    } else {
      searchFilters.is_blocked = value
    }
  }
  const { t } = useI18n()
  const blockedOptions = ref([
    { value: true, label: t('admin_employees_blocked_status_blocked') },
    { value: false, label: t('admin_employees_blocked_status_not_blocked') },
  ])

  const departmentsFilterString = ref('')

  const departmentsWithUnassigned = computed(() => {
    const deps = departments.value || []
    return [{ id: 'unassigned', name: t('admin_employees_department_unassigned') }, ...deps]
  })

  const filteredDepartmentsWithUnassigned = computed(() => {
    if (!departmentsFilterString.value) {
      return departmentsWithUnassigned.value
    }
    const needle = departmentsFilterString.value.toLowerCase()
    return departmentsWithUnassigned.value.filter((dept) => dept.name.toLowerCase().includes(needle))
  })

  const selectedDepartmentIds = computed(() => {
    const result: string[] = []
    if (searchFilters.department_filter?.ids) {
      result.push(...searchFilters.department_filter.ids)
    }
    if (searchFilters.department_filter?.show_unassigned) {
      result.push('unassigned')
    }
    return result.length > 0 ? result : undefined
  })

  const tableConfig: QTableProps['columns'] = [
    {
      name: 'employee',
      label: 'admin_employees_table_employee',
      align: 'left',
      field: (row: UserProfileRoleOut) => `${row.firstName} ${row.lastName}`,
    },
    {
      name: 'position',
      label: 'admin_employees_table_position',
      align: 'left',
      field: (row: UserProfileRoleOut) => row.position || '-',
    },
    {
      name: 'department',
      label: 'admin_employees_table_department',
      align: 'left',
      field: (row: UserProfileRoleOut) => row.department?.name || '-',
      classes: () => 'truncate max-w-[200px] test',
    },
    {
      name: 'role',
      label: 'admin_employees_table_role',
      align: 'left',
      field: (row: UserProfileRoleOut) => row.role,
    },
    {
      name: 'actions',
      label: 'admin_employees_table_actions',
      align: 'right',
      field: (row: UserProfileRoleOut) => ({
        id: row.id,
        blocked: row.blocked,
        isProbationPassed: row.isProbationPassed,
      }),
    },
  ]

  const { data: employeesData, isLoading } = useQuery({
    queryKey: ['employees', searchFilters, () => pagination.value.page],
    queryFn: ({ signal }) => {
      return listUserProfilesFullPage({
        page: {
          size: pagination.value.rowsPerPage,
          number: pagination.value.page,
        },
        filter: searchFilters,
        signal,
      })
    },
    select: (response) => {
      pagination.value.rowsNumber = response.totalItems
      return response
    },
  })

  const employees = computed(() => employeesData.value?.userProfiles || [])

  const { data: departments, isLoading: isDepartmentsLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: () => {
      return listDepartmentsPassportIdmIdmPrivateListDepartmentsPost({
        body: {
          filter: {},
        },
        throwOnError: true,
      })
    },
    select: (response) => response.data.departments,
  })

  const departmentFilterFn: QSelectProps['onFilter'] = (val, update) => {
    update(() => {
      departmentsFilterString.value = val
    })
  }

  const gotoItem = (row: UserProfileRoleOut) => {
    router.push(`/admin/employees/${row.id}`)
  }

  const onRequest = (requestProp: { pagination: { page: number; rowsPerPage: number } }) => {
    pagination.value.page = requestProp.pagination.page
    pagination.value.rowsPerPage = requestProp.pagination.rowsPerPage
  }

  const resetFilters = () => {
    Object.assign(searchFilters, {
      query: '',
      department_filter: undefined,
      is_blocked: undefined,
      role: undefined,
    })
    pagination.value.page = 1
  }

  watch(
    searchFilters,
    () => {
      pagination.value.page = 1
    },
    { deep: true },
  )

  const getFullName = (row: UserProfileRoleOut) => {
    return `${row.firstName} ${row.lastName}`
  }
</script>

<template>
  <div class="flex h-full w-full flex-col gap-4 px-4">
    <div class="head max-w-dvw px-4 lg:px-0">
      <div class="content flex items-center justify-between">
        <h2>{{ $t('admin_employees_title') }}</h2>
        <div class="flex gap-2">
          <UButton
            class="flex items-center gap-2 px-2"
            size="small"
            type="secondary"
            @click="$router.push('/admin/employees/new')"
          >
            <PlusIcon
              width="12"
              height="12"
            />
            {{ $t('admin_employees_add_employee') }}
          </UButton>
        </div>
      </div>
    </div>

    <div class="content max-w-dvw px-4 lg:px-0">
      <div class="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center">
        <QInput
          :model-value="searchFilters.query"
          class="flex-1"
          :placeholder="$t('admin_employees_search_placeholder')"
          outlined
          clearable
          dense
          @update:model-value="(value) => handleQueryChange(value as string | null)"
        >
          <template #prepend>
            <SearchIcon />
          </template>
        </QInput>

        <QSelect
          :model-value="selectedDepartmentIds"
          :options="filteredDepartmentsWithUnassigned"
          option-label="name"
          option-value="id"
          :label="$t('admin_employees_department')"
          :loading="isDepartmentsLoading"
          multiple
          outlined
          emit-value
          map-options
          clearable
          dense
          use-input
          input-debounce="300"
          menu-anchor="bottom start"
          popup-content-class="admin-employees-departments-select-filter"
          class="admin-employees-departments-select w-full lg:w-[200px]"
          @update:model-value="(value) => handleDepartmentChange(value as string[] | null)"
          @filter="departmentFilterFn"
        />

        <QSelect
          :model-value="searchFilters.is_blocked"
          :options="blockedOptions"
          option-label="label"
          option-value="value"
          emit-value
          map-options
          clearable
          :label="$t('admin_employees_blocked_status')"
          outlined
          dense
          class="w-full lg:w-[200px]"
          @update:model-value="(value) => handleReturnBlockedChange(value as boolean | null)"
        />

        <UButton
          type="secondary"
          class="flex w-full items-center gap-2 px-2 lg:w-auto"
          @click="resetFilters"
        >
          {{ $t('admin_employees_reset_filters') }}
        </UButton>
      </div>

      <QTable
        v-model:pagination="pagination"
        :columns="tableConfig"
        :rows="employees"
        :loading="isLoading"
        :rows-per-page-options="[0]"
        row-key="id"
        dense
        @request="onRequest"
        @row-click="(_, row) => gotoItem(row)"
      >
        <template #loading>
          <QInnerLoading
            showing
            color="primary"
          />
        </template>

        <template #header-cell="headCellProps">
          <QTh :props="headCellProps">
            {{ $t(headCellProps.col.label) }}
          </QTh>
        </template>

        <template #body-cell-employee="cellProps">
          <QTd>
            <div class="no-wrap flex cursor-pointer items-center gap-2">
              <UserAvatar
                :src="getImage({ id: cellProps.row?.attachments?.[0], quality: 70 })"
                :size="25"
              />

              <div>
                <div class="max-w-[200px] truncate font-medium">
                  {{ getFullName(cellProps.row) }}
                  <QTooltip :delay="500">
                    <div class="max-w-[200px] text-[12px]">
                      {{ getFullName(cellProps.row) }}
                    </div>
                  </QTooltip>
                </div>
                <div class="max-w-[200px] truncate text-sm text-gray-500">
                  {{ cellProps.row.email }}
                  <QTooltip :delay="500">
                    <div class="max-w-[200px] text-[12px]">
                      {{ cellProps.row.email }}
                    </div>
                  </QTooltip>
                </div>
              </div>
            </div>
          </QTd>
        </template>

        <template #body-cell-position="cellProps">
          <QTd class="max-w-[200px] truncate">
            {{ cellProps.row.position || '-' }}
          </QTd>
        </template>

        <template #body-cell-department="cellProps">
          <QTd>
            <div class="max-w-[200px] truncate">
              {{ cellProps.row.department?.name || '-' }}
              <QTooltip :delay="500">
                <div class="max-w-[200px] text-[12px]">
                  {{ cellProps.row.department?.name || '-' }}
                </div>
              </QTooltip>
            </div>
          </QTd>
        </template>

        <template #body-cell-role="cellProps">
          <QTd>
            <div class="flex items-center">
              <div
                class="rounded-full px-2 py-1 text-xs font-medium"
                :class="cellProps.row.role === 'ROLE_ADMIN' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'"
              >
                {{ cellProps.row.role === 'ROLE_ADMIN' ? 'Admin' : 'User' }}
              </div>
            </div>
          </QTd>
        </template>

        <template #header-cell-actions="headCellProps">
          <QTh :props="headCellProps">
            <span />
          </QTh>
        </template>

        <template #body-cell-actions="cellProps">
          <QTd>
            <div class="flex items-center justify-end">
              <div class="flex items-center gap-2">
                <div
                  v-if="cellProps.value.blocked"
                  class="rounded bg-red-100 px-2 py-1 text-xs text-red-800"
                >
                  Blocked
                </div>
                <div
                  v-if="cellProps.value.isProbationPassed"
                  class="rounded bg-green-100 px-2 py-1 text-xs text-green-800"
                >
                  Probation Passed
                </div>
              </div>
              <ChevronRighIcon />
            </div>
          </QTd>
        </template>
      </QTable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .q-table {
    @include font-montserrat-400(14px, 24px, var(--dark-black-2));
    max-height: 768px;
    min-height: 150px;
    :deep(th) {
      font-weight: 600;
    }
  }
  :deep(.q-table__bottom) {
    background-color: var(--grey);
  }
  :deep(thead) {
    background-color: var(--grey);
  }
  :global(.admin-employees-departments-select-filter) {
    width: 200px !important;
    white-space: nowrap !important;
    overflow-x: auto !important;
  }
  :global(.q-item__label) {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }
  .admin-employees-departments-select {
    :deep(.q-field__control) {
      max-height: 40px;
      overflow: hidden;
    }
    :deep(.q-field__control-container) {
      max-height: 40px;
      overflow: hidden;
    }
  }
</style>
